import React from 'react';
import styled from '@emotion/styled';
import { colors, mixins } from '../../../core/styles/theme/default';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from 'recharts';
import { rem } from 'polished';
import { useWindowSize } from '../../../core/utils/Hooks';

const StyledChart = styled.div`
  .plot {
    background: ${colors.grey200};
  }

  .recharts-surface {
    background: ${colors.grey200};
    border-bottom: ${rem(1)} solid ${colors.grey500};
  }

  .recharts-cartesian-grid-vertical {
    margin-top: ${rem(48)};

    ${mixins.MQ('medium')} {
      margin-top: ${rem(24)};
    }
  }
`;

const currencyValueFormatter = (value: number) =>
  `~ CHF ${value.toLocaleString('de-CH')}`;
const energyDemandValueFormatter = (value: number) => `~ ${value} kWh / m2 EBF`;

const CustomLabel = ({
  y,
  value,
  name,
  index,
  type,
  windowWidth,
}: {
  y: number;
  value: number;
  name: string;
  index: number;
  type?: string;
  windowWidth?: number;
}) => {
  const isMobile = windowWidth && windowWidth < 768;

  return (
    <text>
      <tspan
        x={14}
        y={y - 12 * (isMobile ? 2.2 : 1)}
        fill={colors.grue800}
        style={{ fontSize: 14 }}
      >
        {name}
      </tspan>
      <tspan
        {...(isMobile ? { x: 14, dy: 20 } : { dx: 10, dy: 1 })}
        style={{ fontSize: 16 }}
        fill={index === 0 ? colors.primarySunYellow800 : colors.purple700}
      >
        {type === 'currency' && currencyValueFormatter(value)}
        {type === 'energy' && energyDemandValueFormatter(value)}
      </tspan>
    </text>
  );
};

const AusAltMachNeuChart = ({ data, type }: AusAltMachNeuChartProps) => {
  const { width: windowWidth } = useWindowSize();

  return (
    <StyledChart>
      <ResponsiveContainer width={'100%'} height={data.length * 90 + 10}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        >
          <YAxis dataKey="name" type="category" hide padding={{ top: 24 }} />
          <XAxis type="number" hide />
          <CartesianGrid
            horizontal={false}
            strokeDasharray="4 4"
            stroke={colors.grey500}
          />
          <Bar
            dataKey="value"
            barSize={32}
            radius={[0, 16, 16, 0]}
            label={
              <CustomLabel
                type={type}
                y={0}
                value={0}
                name={''}
                index={0}
                windowWidth={windowWidth}
              />
            }
          >
            {data.map((entry: any, index: number) => (
              <Cell
                fill={
                  entry.special ? colors.primarySunYellow800 : colors.purple700
                }
                key={`cell-${index}`}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </StyledChart>
  );
};

export type AusAltMachNeuChartProps = {
  data: {
    name: string;
    value: number;
    special?: boolean;
  }[];
  type?: string;
};

export default AusAltMachNeuChart;
